const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MAX_DATE_RANGE_DAYS = 40;
const MAX_ROUTE_EMPLOYEE_IDS = 2000;
const MAX_ROUTE_PAGE = 1000000;

const ROUTE_NAMES = {
  roster: 'ViewRosterFiltered',
  leaves: 'ViewLeavesFiltered'
};

const ALLOWED_FILTER_TYPES = {
  roster: new Set(['all', 'shift', 'cover']),
  leaves: new Set(['all'])
};
const ALLOWED_MARKED_FILTERS = new Set(['all', 'marked', 'notmarked']);

const parseCompanyId = value => {
  if (value === 'all') return { companyId: null, error: null };
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return { companyId: null, error: 'Company ID must be a positive integer or all.' };
  }
  const companyId = Number(value);
  if (!Number.isSafeInteger(companyId) || companyId <= 0) {
    return { companyId: null, error: 'Company ID is invalid.' };
  }
  return { companyId, error: null };
};

export const formatCalendarDate = (dateValue) => {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const createCalendarDate = (dateValue) => {
  if (!ISO_DATE_PATTERN.test(dateValue)) {
    return null;
  }

  const [year, month, day] = dateValue.split('-').map(Number);
  const calendarDate = new Date(year, month - 1, day);
  if (
    calendarDate.getFullYear() !== year
    || calendarDate.getMonth() !== month - 1
    || calendarDate.getDate() !== day
  ) {
    return null;
  }

  return calendarDate;
};

export const getLondonTodayDate = () => {
  const dateParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());
  const partValue = type => dateParts.find(part => part.type === type)?.value;
  return `${partValue('year')}-${partValue('month')}-${partValue('day')}`;
};

const parseEmployeeIds = employeeIdsValue => {
  if (employeeIdsValue === 'all') {
    return { employeeIds: [], error: null };
  }

  if (typeof employeeIdsValue !== 'string' || employeeIdsValue === '') {
    return { employeeIds: [], error: 'Employee IDs are required.' };
  }

  const idSegments = employeeIdsValue.split(',');
  if (idSegments.some(segment => !/^\d+$/.test(segment))) {
    return { employeeIds: [], error: 'Employee IDs must be positive integers separated by commas.' };
  }

  const employeeIds = Array.from(new Set(idSegments.map(Number)))
    .sort((firstId, secondId) => firstId - secondId);
  if (employeeIds.some(employeeId => !Number.isSafeInteger(employeeId) || employeeId <= 0)) {
    return { employeeIds: [], error: 'One or more employee IDs are invalid.' };
  }

  if (employeeIds.length > MAX_ROUTE_EMPLOYEE_IDS) {
    return {
      employeeIds: [],
      error: `A maximum of ${MAX_ROUTE_EMPLOYEE_IDS} employee IDs is allowed.`
    };
  }

  return { employeeIds, error: null };
};

export const parseRosterRouteFilters = (params, viewType = 'roster') => {
  const parsedCompany = parseCompanyId(params.companyId);
  if (parsedCompany.error) {
    return { valid: false, error: parsedCompany.error };
  }

  const parsedEmployees = parseEmployeeIds(params.employeeIds);
  if (parsedEmployees.error) {
    return { valid: false, error: parsedEmployees.error };
  }

  const filterType = typeof params.filterType === 'string'
    ? params.filterType.toLowerCase()
    : '';
  if (!ALLOWED_FILTER_TYPES[viewType]?.has(filterType)) {
    return { valid: false, error: `Invalid ${viewType} type filter.` };
  }

  const markedFilter = viewType === 'roster'
    ? (typeof params.markedFilter === 'string' ? params.markedFilter.toLowerCase() : '')
    : 'all';
  if (!ALLOWED_MARKED_FILTERS.has(markedFilter)) {
    return { valid: false, error: 'Invalid roster marked filter.' };
  }

  if (typeof params.dateRange !== 'string') {
    return { valid: false, error: 'A date range is required.' };
  }

  const dateSegments = params.dateRange.split(',');
  if (dateSegments.length !== 2) {
    return { valid: false, error: 'Date range must contain a start and end date.' };
  }

  const startCalendarDate = createCalendarDate(dateSegments[0]);
  const endCalendarDate = createCalendarDate(dateSegments[1]);
  if (!startCalendarDate || !endCalendarDate) {
    return { valid: false, error: 'Dates must use YYYY-MM-DD format.' };
  }

  const durationDays = Math.round((endCalendarDate - startCalendarDate) / 86400000);
  if (durationDays < 0) {
    return { valid: false, error: 'Start date must be before or equal to end date.' };
  }
  if (durationDays > MAX_DATE_RANGE_DAYS) {
    return { valid: false, error: 'Maximum 40 days roster can be generated.' };
  }

  const pageValue = typeof params.page === 'string' ? params.page : '';
  if (!/^\d+$/.test(pageValue)) {
    return { valid: false, error: 'Page must be a positive integer.' };
  }
  const page = Number(pageValue);
  if (!Number.isSafeInteger(page) || page <= 0 || page > MAX_ROUTE_PAGE) {
    return { valid: false, error: 'Page is outside the supported range.' };
  }

  return {
    valid: true,
    error: null,
    companyId: parsedCompany.companyId,
    employeeIds: parsedEmployees.employeeIds,
    filterType,
    typeCode: filterType.toUpperCase(),
    markedFilter,
    markedCode: markedFilter.toUpperCase(),
    startDate: dateSegments[0],
    endDate: dateSegments[1],
    startCalendarDate,
    endCalendarDate,
    page
  };
};

export const parseRosterSortQuery = query => {
  const sort = typeof query.sort === 'string' ? query.sort.toLowerCase() : 'roster';
  const order = typeof query.order === 'string' ? query.order.toLowerCase() : 'asc';
  if (sort !== 'roster' || !['asc', 'desc'].includes(order)) {
    return { valid: false, error: 'Invalid roster sort options.' };
  }
  return {
    valid: true,
    error: null,
    sort,
    order,
    sortOrder: order === 'asc' ? 1 : -1
  };
};

export const buildRosterRoute = ({
  viewType = 'roster',
  companyId = null,
  employeeIds = [],
  filterType = 'all',
  markedFilter = 'all',
  startDate,
  endDate,
  page = 1,
  sort = 'roster',
  order = 'asc'
}) => ({
  name: ROUTE_NAMES[viewType],
  params: {
    companyId: companyId ? String(companyId) : 'all',
    ...(viewType === 'roster' ? {
      markedFilter: markedFilter.toLowerCase()
    } : {}),
    employeeIds: employeeIds.length
      ? [...new Set(employeeIds)].sort((firstId, secondId) => firstId - secondId).join(',')
      : 'all',
    filterType: filterType.toLowerCase(),
    dateRange: `${startDate},${endDate}`,
    page: String(page)
  },
  query: { sort, order }
});

export const buildDefaultRosterRoute = (viewType = 'roster') => {
  const today = getLondonTodayDate();
  return buildRosterRoute({
    viewType,
    employeeIds: [],
    filterType: 'all',
    startDate: today,
    endDate: today,
    page: 1
  });
};

export const isRosterFilteredRoute = routeName => (
  routeName === ROUTE_NAMES.roster || routeName === ROUTE_NAMES.leaves
);
